import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useAssociados, useAssociadosStats, useCreateAssociado, useDeleteAssociado } from '@/hooks/useAssociados'
import { useNavigate } from 'react-router-dom'
import {
    Search, Plus, Filter, Eye, Edit, Trash2, ChevronLeft, ChevronRight,
    Users as UsersIcon, X, Loader2, RefreshCw, CheckCircle2,
} from 'lucide-react'
import { NovoContratoWizard } from '@/components/admin/NovoContratoWizard'
import { mascararCPF, validarCPF } from '@/lib/validators'
import { formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import type { Associado } from '@/types/database'
import { supabase } from '@/config/supabase'
import { asaasService } from '@/services/asaas.service'

export function AssociadosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('todos')
    const [empresaFilter, setEmpresaFilter] = useState<string>('todas')
    const [page, setPage] = useState(1)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showWizardFor, setShowWizardFor] = useState<Associado | null>(null)
    const navigate = useNavigate()

    // Stats reais independentes da paginação
    const { data: stats } = useAssociadosStats()

    // ── Batch Asaas Sync ──────────────────────────────────────
    const [syncState, setSyncState] = useState<{ running: boolean; done: number; total: number; errors: number }>({
        running: false, done: 0, total: 0, errors: 0,
    })
    const [chargeState, setChargeState] = useState<{ running: boolean; done: number; total: number; errors: number }>({
        running: false, done: 0, total: 0, errors: 0,
    })

    const handleSyncAsaas = async () => {
        if (!confirm('Vai buscar todos os associados sem Asaas ID. Pode demorar alguns minutos. Continuar?')) return

        // Busca todos os associados sem asaas_customer_id paginando de 1000 em 1000
        const allData: { id: string; cpf: string; nome: string }[] = []
        let from = 0
        const chunk = 1000
        while (true) {
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_associados')
                .select('id, cpf, nome')
                .is('asaas_customer_id', null)
                .is('deleted_at', null)
                .not('cpf', 'is', null)
                .range(from, from + chunk - 1)
            if (error) { alert(`Erro: ${error.message}`); return }
            if (!data?.length) break
            allData.push(...data)
            if (data.length < chunk) break
            from += chunk
        }

        if (!allData.length) {
            alert('Nenhum associado pendente!')
            return
        }

        setSyncState({ running: true, done: 0, total: allData.length, errors: 0 })
        let erros = 0
        for (let i = 0; i < allData.length; i++) {
            const a = allData[i]!
            try { await asaasService.lookupCustomer(a.cpf, a.id) } catch { erros++ }
            setSyncState(s => ({ ...s, done: i + 1, errors: erros }))
            if (i < allData.length - 1) await new Promise(r => setTimeout(r, 150))
        }
        setSyncState(s => ({ ...s, running: false }))
    }

    // ── Sincronizar Cobranças ─────────────────────────────────
    const handleSyncCharges = async () => {
        if (!confirm('Vai buscar cobranças dos últimos 3 meses e atualizar o status de todos os associados com Asaas ID. Continuar?')) return

        // Pega todos associados com asaas_customer_id
        const allData: { id: string }[] = []
        let from = 0
        while (true) {
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_associados')
                .select('id')
                .not('asaas_customer_id', 'is', null)
                .is('deleted_at', null)
                .range(from, from + 999)
            if (error) { alert(`Erro: ${error.message}`); return }
            if (!data?.length) break
            allData.push(...data)
            if (data.length < 1000) break
            from += 1000
        }

        if (!allData.length) { alert('Nenhum associado com Asaas ID!'); return }

        setChargeState({ running: true, done: 0, total: allData.length, errors: 0 })
        let erros = 0
        for (let i = 0; i < allData.length; i++) {
            try { await asaasService.syncCharges(allData[i]!.id) } catch { erros++ }
            setChargeState(s => ({ ...s, done: i + 1, errors: erros }))
            if (i < allData.length - 1) await new Promise(r => setTimeout(r, 200))
        }
        setChargeState(s => ({ ...s, running: false }))
    }

    useEffect(() => { setPageTitle('Associados') }, [setPageTitle])

    const { data: result, isLoading } = useAssociados({ search, status: statusFilter === 'todos' ? undefined : statusFilter, asaas_empresa: empresaFilter === 'todas' ? undefined : empresaFilter, page, pageSize: 10 })
    const associados = result?.data || []
    const totalPages = result?.totalPages || 1
    const total = result?.total || 0

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Associados</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">{total} registros</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Sincronizar Asaas */}
                    {syncState.running ? (
                        <div className="flex items-center gap-2 text-sm text-surface-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                            <Loader2 size={15} className="animate-spin text-amber-600" />
                            <span className="font-medium">{syncState.done}/{syncState.total}</span>
                            {syncState.errors > 0 && <span className="text-danger-600">({syncState.errors} erros)</span>}
                        </div>
                    ) : syncState.done > 0 ? (
                        <div className="flex items-center gap-2 text-sm text-success-700 bg-success-50 border border-success-200 rounded-xl px-3 py-2">
                            <CheckCircle2 size={15} />
                            <span>{syncState.done} sincronizados{syncState.errors > 0 ? `, ${syncState.errors} erros` : ''}</span>
                        </div>
                    ) : (
                        <button onClick={handleSyncAsaas} className="btn btn-secondary btn-md gap-2">
                            <RefreshCw size={16} /> Sincronizar Asaas
                        </button>
                    )}
                    {chargeState.running ? (
                        <div className="flex items-center gap-2 text-sm text-surface-700 bg-purple-50 border border-purple-200 rounded-xl px-3 py-2">
                            <Loader2 size={15} className="animate-spin text-purple-600" />
                            <span className="font-medium">Cobranças {chargeState.done}/{chargeState.total}</span>
                            {chargeState.errors > 0 && <span className="text-danger-600">({chargeState.errors} erros)</span>}
                        </div>
                    ) : chargeState.done > 0 ? (
                        <div className="flex items-center gap-2 text-sm text-success-700 bg-success-50 border border-success-200 rounded-xl px-3 py-2">
                            <CheckCircle2 size={15} />
                            <span>Cobranças: {chargeState.done} ok{chargeState.errors > 0 ? `, ${chargeState.errors} erros` : ''}</span>
                        </div>
                    ) : (
                        <button onClick={handleSyncCharges} className="btn btn-secondary btn-md gap-2" title="Sync cobranças e status">
                            <RefreshCw size={16} className="text-purple-600" /> Cobranças
                        </button>
                    )}
                    <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-md gap-2">
                        <Plus size={18} /> Novo Associado
                    </button>
                </div>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatusCard label="Ativos" value={stats?.ativo ?? 0} color="success"
                    active={statusFilter === 'ativo'}
                    onClick={() => { setStatusFilter(s => s === 'ativo' ? 'todos' : 'ativo'); setPage(1) }} />
                <StatusCard label="Inadimplentes" value={stats?.inadimplente ?? 0} color="warning"
                    active={statusFilter === 'inadimplente'}
                    onClick={() => { setStatusFilter(s => s === 'inadimplente' ? 'todos' : 'inadimplente'); setPage(1) }} />
                <StatusCard label="Cancelados" value={stats?.cancelado ?? 0} color="danger"
                    active={statusFilter === 'cancelado'}
                    onClick={() => { setStatusFilter(s => s === 'cancelado' ? 'todos' : 'cancelado'); setPage(1) }} />
            </div>

            {/* Filters */}
            <div className="glass-card p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input
                            type="text" value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                            placeholder="Buscar por nome ou CPF..."
                            className="input pl-10"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={16} className="text-surface-700/50" />
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }} className="input w-auto">
                            <option value="todos">Todos</option>
                            <option value="ativo">Ativo</option>
                            <option value="nao_ativado">Não Ativado</option>
                            <option value="inadimplente">Inadimplente</option>
                            <option value="cancelado">Cancelado</option>
                        </select>
                        <select value={empresaFilter} onChange={(e) => { setEmpresaFilter(e.target.value); setPage(1) }} className="input w-auto">
                            <option value="todas">Todas as empresas</option>
                            <option value="ACSM">ACSM</option>
                            <option value="MULTIVEICULAR">Multiveicular</option>
                            <option value="sem">Sem Asaas</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : associados.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <UsersIcon size={40} className="text-surface-700/20 mb-3" />
                        <p className="text-surface-700/60">Nenhum associado encontrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Associado</th>
                                    <th>CPF</th>
                                    <th className="hidden md:table-cell">Telefone</th>
                                    <th className="hidden lg:table-cell">Cidade</th>
                                    <th>Status</th>
                                    <th className="hidden lg:table-cell">Asaas</th>
                                    <th className="hidden lg:table-cell">Desde</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {associados.map((a) => (
                                    <tr key={a.id} className="hover:bg-surface-50 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/associados/${a.id}`)}>
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                    <UsersIcon size={16} className="text-primary-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-surface-900 truncate">{a.nome}</p>
                                                    <p className="text-xs text-surface-700/60 truncate">{a.email || '—'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="font-mono text-sm">{mascararCPF(a.cpf)}</td>
                                        <td className="hidden md:table-cell text-sm text-surface-700">{a.telefone || '—'}</td>
                                        <td className="hidden lg:table-cell text-sm text-surface-700">
                                            {a.endereco ? `${a.endereco.cidade || ''}/${a.endereco.uf || ''}` : '—'}
                                        </td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(a.status)}`}>
                                                {translateStatus(a.status)}
                                            </span>
                                        </td>
                                        <td className="hidden lg:table-cell">
                                            {(a as any).asaas_empresa === 'ACSM' ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">ACSM</span>
                                            ) : (a as any).asaas_empresa === 'MULTIVEICULAR' ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Multiveicular</span>
                                            ) : (
                                                <span className="text-sm text-surface-700/30">—</span>
                                            )}
                                        </td>
                                        <td className="hidden lg:table-cell text-sm text-surface-700/60">
                                            {formatDate(a.created_at)}
                                        </td>
                                        <td className="text-right" onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => navigate(`/admin/associados/${a.id}`)}
                                                    className="p-1.5 rounded-lg hover:bg-primary-50 text-surface-700 hover:text-primary-600 transition-colors" title="Ver">
                                                    <Eye size={16} />
                                                </button>
                                                <button className="p-1.5 rounded-lg hover:bg-primary-50 text-surface-700 hover:text-primary-600 transition-colors" title="Editar">
                                                    <Edit size={16} />
                                                </button>
                                                <DeleteBtn id={a.id} nome={a.nome} />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-surface-100">
                        <p className="text-sm text-surface-700/60">
                            Página {page} de {totalPages} — {total} resultados
                        </p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors">
                                <ChevronLeft size={16} />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showWizardFor && (
                <NovoContratoWizard
                    initialAssociado={showWizardFor}
                    onClose={() => setShowWizardFor(null)}
                />
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CreateAssociadoModal onClose={() => setShowCreateModal(false)} />
            )}
        </div>
    )
}

/* ── Status Card ───────────────────────────────────────── */

function StatusCard({ label, value, color, active, onClick }: {
    label: string; value: number; color: 'success' | 'warning' | 'danger'
    active?: boolean; onClick?: () => void
}) {
    const colors = {
        success: active
            ? 'bg-success-100 border-success-400 text-success-800 shadow-md shadow-success-100'
            : 'bg-success-50 border-success-200 text-success-700 hover:bg-success-100',
        warning: active
            ? 'bg-warning-100 border-warning-400 text-warning-800 shadow-md shadow-warning-100'
            : 'bg-warning-50 border-warning-200 text-warning-700 hover:bg-warning-100',
        danger: active
            ? 'bg-danger-100 border-danger-400 text-danger-800 shadow-md shadow-danger-100'
            : 'bg-danger-50 border-danger-200 text-danger-700 hover:bg-danger-100',
    }
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full rounded-xl border p-4 text-left transition-all ${colors[color]} ${onClick ? 'cursor-pointer' : ''}`}
        >
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm font-medium opacity-80">{label}</p>
            {active && <p className="text-[10px] font-bold mt-1 opacity-60">▲ clique para remover filtro</p>}
        </button>
    )
}



/* ── Delete Button ─────────────────────────────────────── */

function DeleteBtn({ id, nome }: { id: string; nome: string }) {
    const deleteMutation = useDeleteAssociado()
    const handleDelete = () => {
        if (confirm(`Tem certeza que deseja excluir "${nome}"?`)) {
            deleteMutation.mutate(id)
        }
    }
    return (
        <button onClick={handleDelete}
            className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-700 hover:text-danger-600 transition-colors" title="Excluir">
            <Trash2 size={16} />
        </button>
    )
}

/* ── Create Modal ──────────────────────────────────────── */

function CreateAssociadoModal({ onClose }: { onClose: () => void }) {
    const createMutation = useCreateAssociado()
    const [form, setForm] = useState({
        nome: '', cpf: '', data_nascimento: '', telefone: '', email: '',
        cidade: '', uf: '',
    })
    const [error, setError] = useState('')

    const set = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }))

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (!form.nome.trim() || !form.cpf.trim() || !form.data_nascimento) {
            setError('Preencha os campos obrigatórios: Nome, CPF e Data de Nascimento.')
            return
        }
        const cpfLimpo = form.cpf.replace(/\D/g, '')
        if (!validarCPF(cpfLimpo)) {
            setError('CPF inválido.')
            return
        }

        createMutation.mutate({
            nome: form.nome.trim(),
            cpf: cpfLimpo,
            data_nascimento: form.data_nascimento,
            telefone: form.telefone || null,
            email: form.email || null,
            endereco: { cidade: form.cidade, uf: form.uf },
            status: 'ativo',
        }, {
            onSuccess: () => onClose(),
            onError: (err) => setError(String(err)),
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white p-6 w-full max-w-lg mx-4 rounded-3xl shadow-elevated overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-surface-900">Novo Associado</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100"><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm">{error}</div>
                    )}

                    <div>
                        <label className="text-sm font-medium text-surface-700 mb-1 block">Nome completo *</label>
                        <input className="input" value={form.nome} onChange={e => set('nome', e.target.value)} autoFocus />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-surface-700 mb-1 block">CPF *</label>
                            <input className="input font-mono" placeholder="000.000.000-00"
                                value={form.cpf} onChange={e => set('cpf', e.target.value)} />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-surface-700 mb-1 block">Nascimento *</label>
                            <input className="input" type="date" value={form.data_nascimento}
                                onChange={e => set('data_nascimento', e.target.value)} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-surface-700 mb-1 block">Telefone</label>
                            <input className="input" value={form.telefone} onChange={e => set('telefone', e.target.value)} />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-surface-700 mb-1 block">E-mail</label>
                            <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                            <label className="text-sm font-medium text-surface-700 mb-1 block">Cidade</label>
                            <input className="input" value={form.cidade} onChange={e => set('cidade', e.target.value)} />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-surface-700 mb-1 block">UF</label>
                            <input className="input" maxLength={2} value={form.uf}
                                onChange={e => set('uf', e.target.value.toUpperCase())} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-surface-100">
                        <button type="button" onClick={onClose} className="btn btn-secondary btn-md">Cancelar</button>
                        <button type="submit" disabled={createMutation.isPending} className="btn btn-primary btn-md gap-2">
                            {createMutation.isPending && <Loader2 size={16} className="animate-spin" />}
                            Cadastrar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
